/**
 * Normalize a pasted video URL into a safe embed iframe src for major hosts.
 */

export interface VideoEmbed {
  embedUrl: string;
}

function sanitizeYoutubeId(fragment: string): string | null {
  const id = fragment.split(/[?&#]/)[0];
  return id && /^[a-zA-Z0-9_-]{6,}$/.test(id) ? id : null;
}

/** Returns embed URL if the link is recognized; otherwise null (show plain link fallback). */
export function resolveIntroVideoEmbed(rawUrl: string): VideoEmbed | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./i, '').toLowerCase();

  // YouTube
  if (
    host === 'youtube.com' ||
    host === 'm.youtube.com' ||
    host === 'music.youtube.com' ||
    host === 'youtube-nocookie.com'
  ) {
    const nocookie = host === 'youtube-nocookie.com';

    if (url.pathname.startsWith('/embed/')) {
      const id = sanitizeYoutubeId(url.pathname.slice('/embed/'.length));
      if (id) {
        const base = nocookie ? 'https://www.youtube-nocookie.com' : 'https://www.youtube.com';
        return { embedUrl: `${base}/embed/${id}${url.search}` };
      }
    }

    if (url.pathname.startsWith('/shorts/')) {
      const id = sanitizeYoutubeId(url.pathname.split('/')[2] ?? '');
      if (id) {
        return { embedUrl: `https://www.youtube.com/embed/${id}` };
      }
    }

    const v = url.searchParams.get('v');
    if (v) {
      const id = sanitizeYoutubeId(v);
      if (id) {
        return { embedUrl: `https://www.youtube.com/embed/${id}` };
      }
    }

    if (url.pathname.startsWith('/live/')) {
      const id = sanitizeYoutubeId(url.pathname.split('/')[2] ?? '');
      if (id) {
        return { embedUrl: `https://www.youtube.com/embed/${id}` };
      }
    }
  }

  if (host === 'youtu.be') {
    const id = sanitizeYoutubeId(url.pathname.slice(1));
    if (id) {
      return { embedUrl: `https://www.youtube.com/embed/${id}` };
    }
  }

  // Vimeo
  if (host === 'vimeo.com') {
    const parts = url.pathname.split('/').filter(Boolean);
    const numeric = parts.find((p) => /^\d+$/.test(p));
    if (numeric) {
      return { embedUrl: `https://player.vimeo.com/video/${numeric}` };
    }
  }

  if (host === 'player.vimeo.com' && url.pathname.includes('/video/')) {
    const id = url.pathname.split('/video/')[1]?.split('/')[0];
    if (id && /^\d+$/.test(id)) {
      return { embedUrl: `https://player.vimeo.com/video/${id}` };
    }
  }

  // Loom
  if (host === 'loom.com' || host === 'www.loom.com') {
    const shareIdx = url.pathname.indexOf('/share/');
    if (shareIdx >= 0) {
      const id = url.pathname.slice(shareIdx + '/share/'.length).split('/')[0];
      if (id && /^[\w-]+$/.test(id)) {
        return { embedUrl: `https://www.loom.com/embed/${id}` };
      }
    }
    const embedIdx = url.pathname.indexOf('/embed/');
    if (embedIdx >= 0) {
      const id = url.pathname.slice(embedIdx + '/embed/'.length).split('/')[0];
      if (id && /^[\w-]+$/.test(id)) {
        return { embedUrl: `https://www.loom.com/embed/${id}` };
      }
    }
  }

  return null;
}
